import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "./App.css";
import Header from "./components/Header";
import LeagueDashboard from "./components/LeagueDashboard";
import TeamDashboard from "./components/TeamDashboard";
import UserSchedule from "./components/UserSchedule";
import appleIcon from "./assets/external/apple.svg";
import { useAppleSignIn } from "./hooks/useAppleSignIn";
import config from "./config";
import { createSafeUrl } from "./utils/api.js";
import { BrowserRouter as Router } from "react-router-dom";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router";

import { onAuthStateChanged, getRedirectResult } from "firebase/auth";
import { auth } from "./firebase";

// Wrapper for Navigation State
const NavigationBar = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // If on landing page ("/") and not logged in, don't show nav bar?
  // Actually, if we are in guest mode on /league, we probably WANT the nav bar to switch between League/Team.
  // But if we are on "/", we don't want it.
  if (location.pathname === "/") return null;

  const getActiveTab = (pathname) => {
    if (pathname.startsWith("/schedule")) return "schedule";
    if (pathname.startsWith("/league")) return "league";
    if (pathname.startsWith("/team")) return "team";
    return "";
  };

  const activeTab = getActiveTab(location.pathname);

  return (
    <div className="dashboard-nav">
      {user && (
        <button
          type="button"
          className={`nav-btn ${activeTab === "schedule" ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            navigate("/schedule");
          }}
        >
          Schedule
        </button>
      )}
      <button
        type="button"
        className={`nav-btn ${activeTab === "league" ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          navigate("/league");
        }}
      >
        League
      </button>
      <button
        type="button"
        className={`nav-btn ${activeTab === "team" ? "active" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          navigate("/team");
        }}
      >
        Team
      </button>
    </div>
  );
};

NavigationBar.propTypes = {
  user: PropTypes.object,
};

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const { isAppleSdkLoaded, status, setStatus, isLoading, signIn } =
    useAppleSignIn();
  // Removed unused showPhonePrompt state
  const mainContentRef = useRef(null);
  const navigate = useNavigate();

  const verifyWithBackend = useCallback(async (idToken, firebaseUser) => {
    try {
      const response = await fetch(
        createSafeUrl(config.apiBaseUrl, "/users/auth/verify-token"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            idToken: idToken,
            appleCredential: null,
            signupSource: "web",
          }),
        },
      );

      if (response.ok) {
        const apiData = await response.json();
        const apiName =
          (apiData.user?.name && apiData.user.name.trim() !== "Unknown"
            ? apiData.user.name.trim()
            : "") ||
          `${apiData.user?.first_name || ""} ${apiData.user?.last_name || ""}`.trim() ||
          apiData.user?.display_name ||
          firebaseUser.displayName ||
          "";

        const userData = {
          email: apiData.user?.email ?? firebaseUser.email,
          name: apiName,
          id: apiData.user?.user_id ?? apiData.user?.id ?? firebaseUser.uid,
          user_id: apiData.user?.user_id ?? apiData.user?.id,
          firebaseUid: firebaseUser.uid,
          photoUrl: firebaseUser.photoURL,
          phone_number: apiData.user?.phone_number,
          is_super_admin:
            apiData.user?.is_super_admin === true ||
            apiData.is_super_admin === true,
          getIdToken: firebaseUser.getIdToken.bind(firebaseUser),
        };

        setUser(userData);

        // Show phone prompt if phone is missing and they haven't skipped this session
        if (
          !userData.phone_number &&
          !sessionStorage.getItem("skippedPhonePrompt")
        ) {
          // setShowPhonePrompt(true);
        }
      } else {
        // Fallback - preserve admin status if we previously had it
        setUser((prev) => ({
          email: firebaseUser.email,
          name: firebaseUser.displayName || prev?.name || "",
          id: prev?.user_id ?? "Fetching...",
          user_id: prev?.user_id,
          firebaseUid: firebaseUser.uid,
          photoUrl: firebaseUser.photoURL,
          is_super_admin: prev?.is_super_admin || false,
          getIdToken: firebaseUser.getIdToken.bind(firebaseUser),
        }));
      }
    } catch (error) {
      // Intentionally caught to allow fallback user state without crashing
      console.error("Error verifying user:", error);
      setUser((prev) => ({
        email: firebaseUser.email,
        name: firebaseUser.displayName || prev?.name || "",
        id: prev?.user_id ?? "Fetching...",
        user_id: prev?.user_id,
        firebaseUid: firebaseUser.uid,
        photoUrl: firebaseUser.photoURL,
        is_super_admin: prev?.is_super_admin || false,
        getIdToken: firebaseUser.getIdToken.bind(firebaseUser),
      }));
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log("App.js: Mounting and setting up auth listeners");

    // Check for redirect result (Apple Sign In)
    const checkRedirect = async () => {
      try {
        console.log("App.js: Checking for getRedirectResult...");
        const result = await getRedirectResult(auth);
        if (result) {
          console.log("App.js: Redirect Sign-In Successful", result);
          const idToken = await result.user.getIdToken();
          await verifyWithBackend(idToken, result.user);
        }
      } catch (error) {
        // Intentionally swallowed; user may have aborted sign in or network failed
        console.error("App.js: Redirect Sign-In Error:", error);
      }
    };
    checkRedirect();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("App.js: onAuthStateChanged triggered", firebaseUser);
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        await verifyWithBackend(idToken, firebaseUser);
      } else {
        setUser(null);
        setIsAuthLoading(false);
      }
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [navigate, verifyWithBackend]);

  const handleSignOut = () => {
    localStorage.removeItem("pb_mock_user"); // NOSONAR
    auth.signOut();
    setUser(null);
    setStatus("Signed out successfully!");
    navigate("/");
    setTimeout(() => setStatus(""), 3000);
  };

  if (isAuthLoading) {
    return (
      <div className="app-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header user={user} onSignOut={handleSignOut} onSignIn={signIn} />

      <main className="main-content" ref={mainContentRef}>
        <div className="dashboard-section">
          {/* Show Nav Bar if we are NOT on the landing page (user is logged in OR accessing guest routes) */}
          <NavigationBar user={user} />

          <Routes>
            {/* Landing Page */}
            <Route
              path="/"
              element={
                user ? (
                  <Navigate to="/league" replace />
                ) : (
                  <LandingHero
                    user={user}
                    signIn={signIn}
                    isLoading={isLoading}
                    isAppleSdkLoaded={isAppleSdkLoaded}
                    status={status}
                  />
                )
              }
            />

            {/* Guest/User Accessible Routes */}
            <Route
              path="/schedule"
              element={
                user ? (
                  <UserSchedule user={user} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/league" element={<LeagueDashboard user={user} />} />
            <Route
              path="/league/:leagueId"
              element={<LeagueDashboard user={user} />}
            />
            <Route path="/team" element={<TeamDashboard user={user} />} />
            <Route
              path="/team/:teamId"
              element={<TeamDashboard user={user} />}
            />

            {/* Apple Sign-In Redirect Handler */}
            <Route path="/apple-finalize" element={<AppleRedirect />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

const AppleRedirect = () => {
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    try {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        const payload = JSON.parse(decodeURIComponent(hash));
        if (payload.error) {
          setError(payload.error);
        } else {
          import("firebase/auth").then(
            ({ getAuth, OAuthProvider, signInWithCredential }) => {
              const auth = getAuth();
              const provider = new OAuthProvider("apple.com");
              const credential = provider.credential({
                idToken: payload.id_token,
              });
              signInWithCredential(auth, credential)
                .then(() => {
                  window.location.href = "/";
                })
                .catch((err) => {
                  setError(err.message);
                });
            },
          );
        }
      }
    } catch (e) {
      console.error("Failed to parse Apple Sign In response:", e);
      setError("Failed to parse Apple Sign In response.");
    }
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        color: "var(--color-text-primary)",
      }}
    >
      <h2>Completing Sign In...</h2>
      <p style={{ color: "var(--color-text-secondary)" }}>
        {error
          ? `Error: ${error}`
          : "Please wait while authentication completes."}
      </p>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

const LandingHero = ({ user, signIn, isLoading, isAppleSdkLoaded, status }) => {
  const navigate = useNavigate();

  return (
    <div className="hero-section">
      <h1 className="hero-title">Play Ball</h1>
      <p className="hero-subtitle">
        Build more community by simplifying the league management, coach,
        player, &amp; fan experience.
      </p>

      <div
        className="cta-container"
        style={{
          flexDirection: "column",
          gap: "0.75rem",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          className="apple-signin-button"
          onClick={signIn}
          disabled={isLoading || !isAppleSdkLoaded}
        >
          <span className="apple-icon-wrapper">
            <img
              src={appleIcon}
              alt="Apple Logo"
              className="apple-logo"
              style={{
                width: "16px",
                height: "16px",
                filter: "var(--apple-logo-filter)",
              }}
            />
          </span>{" "}
          Sign in with Apple
        </button>

        <div className="explore-options" style={{ marginTop: "8px" }}>
          <button
            type="button"
            className="text-link"
            onClick={() => navigate("/league")}
            style={{
              background: "none",
              border: "none",
              color: "var(--color-text-primary)",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "0.95rem",
            }}
          >
            Continue as guest
          </button>
        </div>
      </div>

      {status && (
        <div className={`status-pill ${user ? "success" : "error"}`}>
          {status}
        </div>
      )}
    </div>
  );
};

// Landing Hero PropTypes
LandingHero.propTypes = {
  user: PropTypes.object,
  signIn: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isAppleSdkLoaded: PropTypes.bool.isRequired,
  status: PropTypes.string,
};

export default App;
